import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import {
  Plus,
  Trash,
  PencilSimple,
  CaretLeft,
  CaretRight,
  Check,
  X,
  Student,
  Clock,
  WarningCircle,
  CalendarBlank,
} from "@phosphor-icons/react";

const DAYS = ["Понедельник", "Вторник", "Среда", "Четверг", "Пятница", "Суббота", "Воскресенье"];
const DAYS_SHORT = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
const CHILDREN = ["Денис", "Саша"];
const MONTHS_RU = ["янв", "фев", "мар", "апр", "мая", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];
const TRACKING_START = "2026-06-22";
const HORIZON_DAYS = 70;

// ---------------- date helpers ----------------
function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function mondayOf(d) { const x = startOfDay(d); const wd = (x.getDay() + 6) % 7; x.setDate(x.getDate() - wd); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function isoDate(d) {
  const x = startOfDay(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
}
function parseISO(s) { if (!s) return null; const [y, m, d] = s.split("-").map(Number); return startOfDay(new Date(y, m - 1, d)); }
function fmtDay(d) { return `${d.getDate()} ${MONTHS_RU[d.getMonth()]}`; }
function fmtMoney(n) { return `${(n || 0).toLocaleString("ru-RU")} ₽`; }

// occurrences of a lesson's weekday within [from, to]
function genOccurrences(lesson, from, to) {
  const out = [];
  let m = mondayOf(from);
  const lastM = mondayOf(to);
  while (m <= lastM) {
    const occ = addDays(m, lesson.day);
    if (occ >= from && occ <= to) out.push(occ);
    m = addDays(m, 7);
  }
  return out;
}

// Сгенерировать партии оплаты для одной схемы (по преподавателю)
function genBatches(plan, lessons, today) {
  const horizon = addDays(today, HORIZON_DAYS);
  const trackingStart = parseISO(TRACKING_START);
  const teacherLessons = lessons.filter(l => l.teacher && l.teacher === plan.teacher);
  if (teacherLessons.length === 0) return [];

  // все занятия преподавателя в горизонте
  let occ = [];
  for (const l of teacherLessons) {
    const start = plan.billing === "package"
      ? (parseISO(plan.anchor_date) || trackingStart)
      : (parseISO(l.start_date) || trackingStart);
    for (const d of genOccurrences(l, start, horizon)) {
      const iso = isoDate(d);
      if ((l.payments || {})[iso] === "skipped") continue; // пропущенное занятие — не в счёт
      occ.push({ date: d, iso, lesson: l });
    }
  }
  occ.sort((a, b) => a.date - b.date || a.lesson.time_seattle.localeCompare(b.lesson.time_seattle));

  const batches = [];
  const mkStatus = (due) => {
    const key = isoDate(due);
    if (plan.paid_batches?.[key]) return "paid";
    return due <= today ? "due" : "upcoming";
  };

  if (plan.billing === "per_session") {
    for (const o of occ) {
      const amount = plan.price_mode === "fixed" ? plan.price : (o.lesson.cost || 0);
      const due = o.date;
      batches.push({ key: o.iso, due, prepay: plan.prepay, amount, sessions: [o], status: mkStatus(due) });
    }
  } else if (plan.billing === "weekly") {
    const groups = new Map();
    for (const o of occ) {
      const wk = isoDate(mondayOf(o.date));
      if (!groups.has(wk)) groups.set(wk, []);
      groups.get(wk).push(o);
    }
    for (const [wk, sessions] of groups) {
      const monday = parseISO(wk);
      const due = addDays(monday, plan.billing_dow);
      const amount = plan.price_mode === "fixed"
        ? plan.price
        : sessions.reduce((s, o) => s + (o.lesson.cost || 0), 0);
      batches.push({ key: isoDate(due), due, prepay: plan.prepay, amount, sessions, status: mkStatus(due) });
    }
  } else if (plan.billing === "package") {
    const N = plan.package_size || 4;
    for (let i = 0; i + N <= occ.length; i += N) {
      const sessions = occ.slice(i, i + N);
      const due = sessions[0].date;
      batches.push({ key: sessions[0].iso, due, prepay: plan.prepay, amount: plan.price, sessions, status: mkStatus(due) });
    }
  }
  batches.sort((a, b) => a.due - b.due);
  return batches;
}

const emptyLesson = (child = "Денис") => ({
  child, subject: "", day: 0, time_moscow: "", time_seattle: "", teacher: "", contact: "", cost: "",
});
const emptyPlan = () => ({
  teacher: "", subject: "", billing: "weekly", prepay: false, billing_dow: 0,
  package_size: 4, price: "", price_mode: "fixed", anchor_date: "",
});

const INPUT = "w-full border border-[#d4d4d8] px-3 py-2 text-sm focus:outline-none focus:border-[#0047FF]";

export default function ChildrenPage() {
  const [lessons, setLessons] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("payments"); // payments | schedule | lessons | plans
  const [weekOffset, setWeekOffset] = useState(0);

  const [lessonForm, setLessonForm] = useState(null); // {data, editing}
  const [planForm, setPlanForm] = useState(null);

  const today = useMemo(() => startOfDay(new Date()), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lr, pr] = await Promise.all([
        api.get("/children/lessons"),
        api.get("/children/plans"),
      ]);
      setLessons(lr.data);
      setPlans(pr.data);
    } catch {
      toast.error("Не удалось загрузить данные");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // партии по всем схемам
  const batchesByPlan = useMemo(() => {
    const map = {};
    for (const p of plans) map[p.id] = genBatches(p, lessons, today);
    return map;
  }, [plans, lessons, today]);

  // индекс: преподаватель + дата занятия -> статус партии (для расписания)
  const sessionStatusIndex = useMemo(() => {
    const idx = {};
    for (const p of plans) {
      for (const b of batchesByPlan[p.id] || []) {
        for (const s of b.sessions) {
          idx[`${p.teacher}|${s.iso}|${s.lesson.id}`] = b.status;
        }
      }
    }
    return idx;
  }, [plans, batchesByPlan]);

  // долг по преподавателям (партии, у которых дата платежа наступила и не оплачены)
  const debt = useMemo(() => {
    const byTeacher = {};
    let total = 0;
    for (const p of plans) {
      let sum = 0;
      for (const b of batchesByPlan[p.id] || []) {
        if (b.status === "due") sum += b.amount;
      }
      if (sum > 0) { byTeacher[p.teacher] = sum; total += sum; }
    }
    return { byTeacher, total };
  }, [plans, batchesByPlan]);

  const markSkip = useCallback(async (lessonId, dateStr, skip) => {
    setLessons(prev => prev.map(l => {
      if (l.id !== lessonId) return l;
      const p = { ...(l.payments || {}) };
      if (skip) p[dateStr] = "skipped"; else delete p[dateStr];
      return { ...l, payments: p };
    }));
    try {
      const { data } = await api.patch(`/children/lessons/${lessonId}/mark`, {
        date: dateStr, status: skip ? "skipped" : "none",
      });
      setLessons(prev => prev.map(l => (l.id === data.id ? data : l)));
    } catch {
      toast.error("Ошибка обновления");
      load();
    }
  }, [load]);

  const markBatch = useCallback(async (planId, key, paid) => {
    setPlans(prev => prev.map(p => {
      if (p.id !== planId) return p;
      const pb = { ...(p.paid_batches || {}) };
      if (paid) pb[key] = true; else delete pb[key];
      return { ...p, paid_batches: pb };
    }));
    try {
      const { data } = await api.patch(`/children/plans/${planId}/batch`, { key, paid });
      setPlans(prev => prev.map(p => (p.id === data.id ? data : p)));
    } catch {
      toast.error("Ошибка обновления");
      load();
    }
  }, [load]);

  // ---- lessons CRUD ----
  const saveLesson = async () => {
    const { data: fd, editing } = lessonForm;
    if (!fd.subject.trim() || !fd.child.trim()) { toast.error("Укажите ребёнка и предмет"); return; }
    const payload = { ...fd, day: Number(fd.day), cost: parseFloat(fd.cost) || 0 };
    try {
      if (editing) {
        const { data } = await api.put(`/children/lessons/${editing.id}`, payload);
        setLessons(prev => prev.map(l => (l.id === data.id ? data : l)));
        toast.success("Занятие обновлено");
      } else {
        const { data } = await api.post("/children/lessons", payload);
        setLessons(prev => [...prev, data]);
        toast.success("Занятие добавлено");
      }
      setLessonForm(null);
    } catch { toast.error("Ошибка сохранения"); }
  };
  const deleteLesson = async (l) => {
    if (!window.confirm(`Удалить занятие «${l.subject}» (${l.child})?`)) return;
    try {
      await api.delete(`/children/lessons/${l.id}`);
      setLessons(prev => prev.filter(x => x.id !== l.id));
      toast.success("Удалено");
    } catch { toast.error("Ошибка удаления"); }
  };

  // ---- plans CRUD ----
  const savePlan = async () => {
    const { data: fd, editing } = planForm;
    if (!fd.teacher.trim()) { toast.error("Укажите преподавателя"); return; }
    const payload = {
      ...fd,
      billing_dow: Number(fd.billing_dow),
      package_size: Number(fd.package_size) || 4,
      price: parseFloat(fd.price) || 0,
    };
    try {
      if (editing) {
        const { data } = await api.put(`/children/plans/${editing.id}`, payload);
        setPlans(prev => prev.map(p => (p.id === data.id ? data : p)));
        toast.success("Схема обновлена");
      } else {
        const { data } = await api.post("/children/plans", payload);
        setPlans(prev => [...prev, data]);
        toast.success("Схема добавлена");
      }
      setPlanForm(null);
    } catch { toast.error("Ошибка сохранения"); }
  };
  const deletePlan = async (p) => {
    if (!window.confirm(`Удалить схему оплаты «${p.teacher} · ${p.subject}»?`)) return;
    try {
      await api.delete(`/children/plans/${p.id}`);
      setPlans(prev => prev.filter(x => x.id !== p.id));
      toast.success("Удалено");
    } catch { toast.error("Ошибка удаления"); }
  };

  return (
    <div className="p-6 lg:p-10">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="mono-label mb-2">СЕКЦИЯ / СЕМЬЯ</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Дети</h1>
          <p className="text-sm text-[#52525b] mt-2">
            Расписание и оплата занятий партиями. Время: Москва · Сиэтл.
          </p>
        </div>
        {debt.total > 0 && (
          <div className="bg-white border border-[#d4d4d8] px-5 py-3 border-l-4 border-l-[#FF2A00]">
            <div className="mono-label">ДОЛГ СЕЙЧАС</div>
            <div className="font-mono text-2xl font-semibold text-[#FF2A00]">{fmtMoney(debt.total)}</div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="mt-6 flex items-center gap-2 flex-wrap border-b border-[#d4d4d8]">
        <Tab active={tab === "payments"} onClick={() => setTab("payments")}>Платежи</Tab>
        <Tab active={tab === "schedule"} onClick={() => setTab("schedule")}>Расписание</Tab>
        <Tab active={tab === "lessons"} onClick={() => setTab("lessons")}>Занятия</Tab>
      </div>

      {loading && <div className="mt-8 mono-label">ЗАГРУЗКА...</div>}

      {!loading && tab === "payments" && (
        <PaymentsTab
          plans={plans}
          batchesByPlan={batchesByPlan}
          debt={debt}
          today={today}
          onMark={markBatch}
        />
      )}

      {!loading && tab === "schedule" && (
        <ScheduleTab
          lessons={lessons}
          plans={plans}
          sessionStatusIndex={sessionStatusIndex}
          today={today}
          weekOffset={weekOffset}
          setWeekOffset={setWeekOffset}
          onSkip={markSkip}
        />
      )}

      {!loading && tab === "lessons" && (
        <LessonsTab
          lessons={lessons}
          onAdd={() => setLessonForm({ data: emptyLesson(), editing: null })}
          onEdit={(l) => setLessonForm({ data: { ...l, cost: String(l.cost ?? "") }, editing: l })}
          onDelete={deleteLesson}
        />
      )}

      {lessonForm && (
        <LessonForm
          state={lessonForm}
          setState={setLessonForm}
          onClose={() => setLessonForm(null)}
          onSave={saveLesson}
        />
      )}
      {planForm && (
        <PlanForm
          state={planForm}
          setState={setPlanForm}
          onClose={() => setPlanForm(null)}
          onSave={savePlan}
        />
      )}
    </div>
  );
}

function Tab({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
        active ? "border-[#0047FF] text-[#0047FF] font-medium" : "border-transparent text-[#52525b] hover:text-[#0a0a0a]"
      }`}
    >
      {children}
    </button>
  );
}

const BATCH_META = {
  paid: { label: "Оплачено", cls: "text-[#00994C]" },
  due: { label: "К оплате", cls: "text-[#FF2A00]" },
  upcoming: { label: "Предстоит", cls: "text-[#52525b]" },
};

function batchPeriod(b) {
  const first = b.sessions[0].date;
  const last = b.sessions[b.sessions.length - 1].date;
  if (b.sessions.length === 1) return fmtDay(first);
  return `${fmtDay(first)} — ${fmtDay(last)}`;
}

// ============================ PAYMENTS TAB ============================
function PaymentsTab({ plans, batchesByPlan, debt, today, onMark }) {
  const teacherless = plans.length === 0;
  const subjectByTeacher = useMemo(() => {
    const m = {};
    for (const p of plans) m[p.teacher] = p.subject;
    return m;
  }, [plans]);
  const withSubject = (t) => (subjectByTeacher[t] ? `${t} (${subjectByTeacher[t]})` : t);
  return (
    <div className="mt-6 space-y-6">
      {/* Debt by teacher */}
      <div className="bg-white border border-[#d4d4d8] p-4">
        <div className="mono-label mb-3">ДОЛГ ПО ПРЕПОДАВАТЕЛЯМ</div>
        {Object.keys(debt.byTeacher).length === 0 ? (
          <div className="font-mono text-sm text-[#00994C]">Долгов нет</div>
        ) : (
          <div className="space-y-1.5">
            {Object.entries(debt.byTeacher).sort((a, b) => b[1] - a[1]).map(([t, amt]) => (
              <div key={t} className="flex items-center justify-between text-sm">
                <span className="text-[#52525b]">{withSubject(t)}</span>
                <span className="font-mono font-semibold text-[#FF2A00]">{fmtMoney(amt)}</span>
              </div>
            ))}
            <div className="flex items-center justify-between text-sm border-t border-[#d4d4d8] pt-1.5 mt-1.5">
              <span className="mono-label">ИТОГО</span>
              <span className="font-mono font-semibold">{fmtMoney(debt.total)}</span>
            </div>
          </div>
        )}
      </div>

      {teacherless && (
        <div className="bg-white border border-[#d4d4d8] p-10 text-center">
          <WarningCircle size={32} weight="duotone" className="mx-auto text-[#52525b]" />
          <h3 className="text-lg font-medium mt-3">Схем оплаты нет</h3>
          <p className="text-sm text-[#52525b] mt-1">Добавьте схемы на вкладке «Схемы оплаты»</p>
        </div>
      )}

      {/* Batches per teacher */}
      {plans.map(p => {
        const batches = batchesByPlan[p.id] || [];
        // показываем оплаченные/к оплате + ближайшие предстоящие
        const due = batches.filter(b => b.status === "due");
        const paid = batches.filter(b => b.status === "paid");
        const upcoming = batches.filter(b => b.status === "upcoming").slice(0, 3);
        const shown = [...due, ...upcoming, ...paid].sort((a, b) => a.due - b.due);
        if (batches.length === 0) return null;
        return (
          <div key={p.id} className="bg-white border border-[#d4d4d8]">
            <div className="px-5 py-3 bg-[#f5f5f4] border-b border-[#d4d4d8] flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="font-semibold">{p.teacher} <span className="text-[#52525b] font-normal">· {p.subject}</span></div>
                <div className="mono-label mt-0.5">{planSummary(p)}</div>
              </div>
            </div>
            <div className="divide-y divide-[#d4d4d8]">
              {shown.map(b => {
                const meta = BATCH_META[b.status];
                return (
                  <div key={b.key} className="px-5 py-3 flex items-center justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 font-medium">
                        <CalendarBlank size={13} weight="bold" className="text-[#52525b]" />
                        {batchPeriod(b)}
                        <span className="text-xs text-[#52525b] font-normal">
                          · {b.sessions.length} зан. · {b.prepay ? "вперёд" : "после"} · платёж {fmtDay(b.due)}
                        </span>
                      </div>
                      <div className="text-xs text-[#52525b] mt-0.5 truncate">
                        {b.sessions.map(s => `${s.lesson.child} ${DAYS_SHORT[s.lesson.day]}`).join(", ")}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="font-mono font-semibold">{fmtMoney(b.amount)}</span>
                      {b.status === "upcoming" ? (
                        <span className="inline-flex items-center gap-1.5 text-xs text-[#52525b] border border-[#d4d4d8] px-2.5 py-1.5">
                          <Clock size={12} weight="bold" />Предстоит
                        </span>
                      ) : b.status === "paid" ? (
                        <button
                          onClick={() => onMark(p.id, b.key, false)}
                          className="inline-flex items-center gap-1.5 text-xs bg-[#00994C] text-white px-2.5 py-1.5 hover:bg-[#007a3d] transition-colors"
                          title="Снять отметку оплаты"
                        >
                          <Check size={12} weight="bold" />Оплачено
                        </button>
                      ) : (
                        <button
                          onClick={() => onMark(p.id, b.key, true)}
                          className="inline-flex items-center gap-1.5 text-xs border border-[#00994C] text-[#00994C] px-2.5 py-1.5 hover:bg-[#00994C] hover:text-white transition-colors"
                        >
                          <Check size={12} weight="bold" />Оплатить
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function planSummary(p) {
  if (p.billing === "per_session") return `ПО ЗАНЯТИЮ · ${p.prepay ? "ВПЕРЁД" : "В ДЕНЬ"}`;
  if (p.billing === "weekly") return `НЕДЕЛЯ · ${p.prepay ? "ВПЕРЁД" : "ПОСЛЕ"} · ПЛАТЁЖ ${DAYS_SHORT[p.billing_dow].toUpperCase()}`;
  return `АБОНЕМЕНТ ${p.package_size} · ВПЕРЁД · ${fmtMoney(p.price)}`;
}

// ============================ SCHEDULE TAB ============================
const CHILD_COLOR = {
  "Денис": "bg-blue-100 text-blue-800 border-blue-200",
  "Саша": "bg-purple-100 text-purple-800 border-purple-200",
};
const SESSION_STATUS = {
  paid: { label: "оплачено", cls: "text-[#00994C]" },
  due: { label: "к оплате", cls: "text-[#FF2A00]" },
  upcoming: { label: "предстоит", cls: "text-[#52525b]" },
};

function ScheduleTab({ lessons, plans, sessionStatusIndex, today, weekOffset, setWeekOffset, onSkip }) {
  const [childFilter, setChildFilter] = useState("all");
  const weekStart = useMemo(() => addDays(mondayOf(today), weekOffset * 7), [today, weekOffset]);
  const weekEnd = useMemo(() => addDays(weekStart, 6), [weekStart]);
  const teacherByName = useMemo(() => {
    const m = {}; for (const p of plans) m[p.teacher] = p; return m;
  }, [plans]);

  const visible = lessons.filter(l => childFilter === "all" || l.child === childFilter);
  const byDay = {};
  for (let d = 0; d < 7; d++) byDay[d] = [];
  for (const l of visible) byDay[l.day]?.push(l);

  return (
    <div className="mt-6">
      <div className="flex items-center gap-2 mb-4">
        <FilterTab active={childFilter === "all"} onClick={() => setChildFilter("all")}>Все</FilterTab>
        {CHILDREN.map(c => <FilterTab key={c} active={childFilter === c} onClick={() => setChildFilter(c)}>{c}</FilterTab>)}
      </div>

      <div className="bg-white border border-[#d4d4d8] p-4 flex items-center gap-2">
        <button onClick={() => setWeekOffset(w => w - 1)} className="p-2 border border-[#d4d4d8] hover:bg-[#18181b] hover:text-white hover:border-[#18181b] transition-colors"><CaretLeft size={14} weight="bold" /></button>
        <div className="min-w-[200px] text-center">
          <div className="mono-label">НЕДЕЛЯ</div>
          <div className="text-base font-semibold">{fmtDay(weekStart)} — {fmtDay(weekEnd)}</div>
        </div>
        <button onClick={() => setWeekOffset(w => w + 1)} className="p-2 border border-[#d4d4d8] hover:bg-[#18181b] hover:text-white hover:border-[#18181b] transition-colors"><CaretRight size={14} weight="bold" /></button>
        {weekOffset !== 0 && <button onClick={() => setWeekOffset(0)} className="ml-2 text-xs border border-[#18181b] px-2.5 py-1.5 hover:bg-[#18181b] hover:text-white transition-colors">Эта неделя</button>}
      </div>

      <div className="mt-4 space-y-4">
        {DAYS.map((dayName, dayIdx) => {
          const dayLessons = (byDay[dayIdx] || []).slice().sort((a, b) => a.time_seattle.localeCompare(b.time_seattle));
          if (dayLessons.length === 0) return null;
          const dayDate = addDays(weekStart, dayIdx);
          const dayISO = isoDate(dayDate);
          return (
            <div key={dayIdx} className="bg-white border border-[#d4d4d8]">
              <div className="px-5 py-2.5 bg-[#f5f5f4] border-b border-[#d4d4d8] flex items-center justify-between">
                <div className="font-semibold">{dayName}</div>
                <div className="mono-label">{fmtDay(dayDate)}</div>
              </div>
              <div className="divide-y divide-[#d4d4d8]">
                {dayLessons.map(l => {
                  const skipped = (l.payments || {})[dayISO] === "skipped";
                  const hasPlan = !!teacherByName[l.teacher];
                  const status = hasPlan ? sessionStatusIndex[`${l.teacher}|${dayISO}|${l.id}`] : null;
                  const st = status ? SESSION_STATUS[status] : null;
                  const isPast = dayDate <= today;
                  return (
                    <div key={l.id} className={`px-5 py-3 flex items-center justify-between gap-4 flex-wrap ${skipped ? "opacity-60" : ""}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border shrink-0 ${CHILD_COLOR[l.child] || "bg-[#f5f5f4] text-[#52525b] border-[#d4d4d8]"}`}>{l.child}</span>
                        <div className="min-w-0">
                          <div className={`font-medium flex items-center gap-2 flex-wrap ${skipped ? "line-through" : ""}`}>
                            {l.subject}
                            <span className="font-mono text-xs text-[#52525b] font-normal">{l.time_moscow} · {l.time_seattle}</span>
                          </div>
                          <div className="text-xs text-[#52525b]">
                            {l.teacher
                              ? <>{l.teacher}{l.contact ? ` · ${l.contact}` : ""}{l.cost ? ` · ${fmtMoney(l.cost)}` : ""}</>
                              : <span className="text-[#FF8A00] inline-flex items-center gap-1"><WarningCircle size={11} weight="fill" />найти преподавателя</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        {skipped ? (
                          <button onClick={() => onSkip(l.id, dayISO, false)} className="text-xs text-[#52525b] hover:text-[#0a0a0a] transition-colors" title="Вернуть занятие в расчёт">
                            пропущено · вернуть
                          </button>
                        ) : (
                          <>
                            {st && <span className={`text-xs font-medium ${st.cls}`}>{st.label}</span>}
                            {isPast && l.teacher && (
                              <button onClick={() => onSkip(l.id, dayISO, true)} className="text-xs text-[#52525b] hover:text-[#FF8A00] transition-colors" title="Отметить пропуск — занятие не пойдёт в оплату">
                                пропуск
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
        {visible.length === 0 && (
          <div className="bg-white border border-[#d4d4d8] p-10 text-center">
            <Student size={32} weight="duotone" className="mx-auto text-[#52525b]" />
            <h3 className="text-lg font-medium mt-3">Занятий нет</h3>
          </div>
        )}
      </div>
    </div>
  );
}

function FilterTab({ active, onClick, children }) {
  return (
    <button onClick={onClick} className={`px-4 py-1.5 text-sm border transition-colors ${active ? "bg-[#0047FF] text-white border-[#0047FF]" : "bg-white border-[#d4d4d8] hover:bg-[#f5f5f4]"}`}>{children}</button>
  );
}

// ============================ LESSONS TAB ============================
function LessonsTab({ lessons, onAdd, onEdit, onDelete }) {
  const sorted = [...lessons].sort((a, b) => a.day - b.day || a.time_seattle.localeCompare(b.time_seattle));
  return (
    <div className="mt-6">
      <div className="flex justify-end mb-4">
        <button onClick={onAdd} className="inline-flex items-center gap-2 bg-[#0047FF] text-white px-4 py-2 text-sm hover:bg-[#0036CC] transition-colors"><Plus size={14} weight="bold" />Добавить занятие</button>
      </div>
      <div className="border border-[#d4d4d8] bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#f5f5f4] border-b border-[#d4d4d8]">
              <th className="text-left px-4 py-2.5 mono-label">День</th>
              <th className="text-left px-4 py-2.5 mono-label">Предмет</th>
              <th className="text-left px-4 py-2.5 mono-label">Ребёнок</th>
              <th className="text-left px-4 py-2.5 mono-label">МСК · Сиэтл</th>
              <th className="text-left px-4 py-2.5 mono-label">Преподаватель</th>
              <th className="text-right px-4 py-2.5 mono-label">Цена</th>
              <th className="text-right px-4 py-2.5 mono-label">Действия</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map(l => (
              <tr key={l.id} className="border-b border-[#d4d4d8] last:border-b-0 hover:bg-[#f5f5f4]">
                <td className="px-4 py-3">{DAYS_SHORT[l.day]}</td>
                <td className="px-4 py-3 font-medium">{l.subject}</td>
                <td className="px-4 py-3">{l.child}</td>
                <td className="px-4 py-3 font-mono text-xs text-[#52525b]">{l.time_moscow} · {l.time_seattle}</td>
                <td className="px-4 py-3">{l.teacher ? <span>{l.teacher}{l.contact ? <span className="text-[#52525b] text-xs"> · {l.contact}</span> : null}</span> : <span className="text-[#FF8A00] text-xs inline-flex items-center gap-1"><WarningCircle size={12} weight="fill" />найти</span>}</td>
                <td className="px-4 py-3 text-right font-mono">{l.cost ? fmtMoney(l.cost) : "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => onEdit(l)} className="inline-flex items-center gap-1 text-xs border border-[#18181b] px-2.5 py-1.5 hover:bg-[#18181b] hover:text-white transition-colors"><PencilSimple size={12} weight="bold" />Ред.</button>
                    <button onClick={() => onDelete(l)} className="p-1.5 text-[#52525b] hover:text-[#FF2A00] transition-colors" title="Удалить"><Trash size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================ PLANS TAB ============================
function PlansTab({ plans, batchesByPlan, onAdd, onEdit, onDelete }) {
  return (
    <div className="mt-6">
      <div className="flex justify-end mb-4">
        <button onClick={onAdd} className="inline-flex items-center gap-2 bg-[#0047FF] text-white px-4 py-2 text-sm hover:bg-[#0036CC] transition-colors"><Plus size={14} weight="bold" />Добавить схему</button>
      </div>
      {plans.length === 0 ? (
        <div className="bg-white border border-[#d4d4d8] p-10 text-center text-sm text-[#52525b]">Схем оплаты пока нет</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {plans.map(p => {
            const batches = batchesByPlan[p.id] || [];
            const dueCount = batches.filter(b => b.status === "due").length;
            return (
              <div key={p.id} className="bg-white border border-[#d4d4d8] p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold">{p.teacher} <span className="text-[#52525b] font-normal">· {p.subject}</span></div>
                    <div className="mono-label mt-1">{planSummary(p)}</div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => onEdit(p)} className="inline-flex items-center gap-1 text-xs border border-[#18181b] px-2.5 py-1.5 hover:bg-[#18181b] hover:text-white transition-colors"><PencilSimple size={12} weight="bold" />Ред.</button>
                    <button onClick={() => onDelete(p)} className="p-1.5 text-[#52525b] hover:text-[#FF2A00] transition-colors" title="Удалить"><Trash size={14} /></button>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-[#d4d4d8] text-xs text-[#52525b] flex gap-4">
                  <span>цена партии: <b className="text-[#0a0a0a] font-mono">{p.price_mode === "fixed" ? fmtMoney(p.price) : "кол-во × цена"}</b></span>
                  {dueCount > 0 && <span className="text-[#FF2A00]">к оплате: {dueCount}</span>}
                  {p.anchor_date && <span>старт: {fmtDay(parseISO(p.anchor_date))}</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================ FORMS ============================
function Field({ label, children }) {
  return <div><div className="mono-label mb-1.5">{label}</div>{children}</div>;
}

function LessonForm({ state, setState, onClose, onSave }) {
  const fd = state.data;
  const set = (f, v) => setState(s => ({ ...s, data: { ...s.data, [f]: v } }));
  return (
    <Modal title={state.editing ? "Редактировать занятие" : "Новое занятие"} onClose={onClose} onSave={onSave} saveLabel={state.editing ? "Сохранить" : "Добавить"}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="РЕБЁНОК"><select value={fd.child} onChange={e => set("child", e.target.value)} className={`${INPUT} bg-white`}>{CHILDREN.map(c => <option key={c} value={c}>{c}</option>)}</select></Field>
        <Field label="ДЕНЬ НЕДЕЛИ"><select value={fd.day} onChange={e => set("day", e.target.value)} className={`${INPUT} bg-white`}>{DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}</select></Field>
      </div>
      <Field label="ПРЕДМЕТ"><input type="text" value={fd.subject} onChange={e => set("subject", e.target.value)} placeholder="Шахматы" className={INPUT} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="ВРЕМЯ МСК"><input type="text" value={fd.time_moscow} onChange={e => set("time_moscow", e.target.value)} placeholder="20:00" className={`${INPUT} font-mono`} /></Field>
        <Field label="ВРЕМЯ СИЭТЛ"><input type="text" value={fd.time_seattle} onChange={e => set("time_seattle", e.target.value)} placeholder="10:00" className={`${INPUT} font-mono`} /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="ПРЕПОДАВАТЕЛЬ"><input type="text" value={fd.teacher} onChange={e => set("teacher", e.target.value)} placeholder="Дмитрий" className={INPUT} /></Field>
        <Field label="СВЯЗЬ"><input type="text" value={fd.contact} onChange={e => set("contact", e.target.value)} placeholder="WhatsApp / Max" className={INPUT} /></Field>
      </div>
      <Field label="ЦЕНА ЗА ЗАНЯТИЕ (₽)"><input type="number" min="0" step="50" value={fd.cost} onChange={e => set("cost", e.target.value)} placeholder="900" className={`${INPUT} font-mono`} /></Field>
    </Modal>
  );
}

function PlanForm({ state, setState, onClose, onSave }) {
  const fd = state.data;
  const set = (f, v) => setState(s => ({ ...s, data: { ...s.data, [f]: v } }));
  return (
    <Modal title={state.editing ? "Редактировать схему" : "Новая схема оплаты"} onClose={onClose} onSave={onSave} saveLabel={state.editing ? "Сохранить" : "Добавить"}>
      <div className="grid grid-cols-2 gap-3">
        <Field label="ПРЕПОДАВАТЕЛЬ"><input type="text" value={fd.teacher} onChange={e => set("teacher", e.target.value)} placeholder="Дмитрий" className={INPUT} /></Field>
        <Field label="ПРЕДМЕТ"><input type="text" value={fd.subject} onChange={e => set("subject", e.target.value)} placeholder="Шахматы" className={INPUT} /></Field>
      </div>
      <Field label="ТИП ОПЛАТЫ">
        <select value={fd.billing} onChange={e => set("billing", e.target.value)} className={`${INPUT} bg-white`}>
          <option value="per_session">По занятию</option>
          <option value="weekly">Неделя</option>
          <option value="package">Абонемент</option>
        </select>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="ОПЛАТА">
          <select value={fd.prepay ? "1" : "0"} onChange={e => set("prepay", e.target.value === "1")} className={`${INPUT} bg-white`}>
            <option value="0">После / в день</option>
            <option value="1">Вперёд</option>
          </select>
        </Field>
        {fd.billing === "weekly" && (
          <Field label="ДЕНЬ ПЛАТЕЖА">
            <select value={fd.billing_dow} onChange={e => set("billing_dow", e.target.value)} className={`${INPUT} bg-white`}>{DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}</select>
          </Field>
        )}
        {fd.billing === "package" && (
          <Field label="ЗАНЯТИЙ В АБОНЕМЕНТЕ"><input type="number" min="1" value={fd.package_size} onChange={e => set("package_size", e.target.value)} className={`${INPUT} font-mono`} /></Field>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="ЦЕНА">
          <select value={fd.price_mode} onChange={e => set("price_mode", e.target.value)} className={`${INPUT} bg-white`}>
            <option value="fixed">Фиксированная за партию</option>
            <option value="per_session">Кол-во × цена занятия</option>
          </select>
        </Field>
        {fd.price_mode === "fixed" && (
          <Field label="СУММА ПАРТИИ (₽)"><input type="number" min="0" step="50" value={fd.price} onChange={e => set("price", e.target.value)} placeholder="3600" className={`${INPUT} font-mono`} /></Field>
        )}
      </div>
      {fd.billing === "package" && (
        <Field label="СТАРТ АБОНЕМЕНТА (дата первого занятия)"><input type="date" value={fd.anchor_date} onChange={e => set("anchor_date", e.target.value)} className={INPUT} /></Field>
      )}
    </Modal>
  );
}

function Modal({ title, children, onClose, onSave, saveLabel }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 overflow-y-auto py-8 px-4">
      <div className="bg-white w-full max-w-lg border border-[#d4d4d8] my-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#d4d4d8]">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose} className="text-[#52525b] hover:text-[#0a0a0a]"><X size={20} /></button>
        </div>
        <div className="px-6 py-5 space-y-4">{children}</div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#d4d4d8]">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-[#d4d4d8] hover:bg-[#f5f5f4] transition-colors">Отмена</button>
          <button onClick={onSave} className="px-4 py-2 text-sm bg-[#0047FF] text-white hover:bg-[#0036CC] transition-colors">{saveLabel}</button>
        </div>
      </div>
    </div>
  );
}
